import cx from "classnames";
import { Location } from "history";
import React from "react";
import { ListRowProps } from "react-virtualized";

import GroupGridItem, {
    GroupGridItemPlaceholder,
} from "@/mk2/apps/groups/components/GroupGridItem";
import { InfiniteListLoadable } from "@/mk2/components/InfiniteListLoadable";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { GroupEntity, UserEntity } from "@/mk2/schemas";

import styles from "./GroupsGrid.mscss";

const ROW_HEIGHT = 312;

interface OwnProps {
    after?: number;
    askedForMembership: number[];
    category?: string;
    className?: string;
    groups: GroupEntity[];
    hasNext?: number;
    hasPrevious?: number;
    joining: number[];
    loadingState: LoadingState;
    location: Location;
    memberships: number[];
    isMobile: boolean;
    query?: string;
    requestUser: UserEntity;
    type?: string;
    newBadge?: boolean;

    onJoinGroup(groupId: number);
    onLoad?(groupType: string, category: string, query: string, after?: number);
}

type Props = OwnProps;

export default class GroupsGrid extends React.PureComponent<Props> {
    public render() {
        const { className, loadingState, groups } = this.props;

        return (
            <div className={cx(styles.GroupsGrid, className)}>
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderInit={this.renderInit}
                    onRenderSuccess={this.renderLoadSuccess}
                    hasData={groups && groups.length > 0}
                />
            </div>
        );
    }

    private load = () => {
        const { onLoad, type, category, query, after } = this.props;
        if (onLoad) {
            onLoad(type, category, query, after);
        }
    };

    private loadNext = () => {
        const { onLoad, type, category, query, groups, loadingState } = this.props;

        // Do not trigger next load
        if (loadingState === LoadingState.LOADING || !onLoad) {
            return;
        }

        const after = groups[groups.length - 1].id;
        onLoad(type, category, query, after);

        return new Promise((resolve) => {
            setTimeout(resolve, 500);
        });
    };

    private renderInit = () => {
        const row = [];
        while (row.length < this.getRowItemsCount()) {
            row.push(
                <GroupGridItemPlaceholder
                    key={`placeholder-${row.length}`}
                    className={styles.GroupsGrid__row__item}
                />,
            );
        }

        return (
            <>
                <div className={styles.GroupsGrid__row}>{row}</div>
                <div className={styles.GroupsGrid__row}>{row}</div>
                <div className={styles.GroupsGrid__row}>{row}</div>
            </>
        );
    };

    private renderLoadSuccess = () => {
        const { groups, hasNext, isMobile, onLoad } = this.props;

        const rowItems = this.getRowItemsCount();
        const realRowsCount =
            Math.floor(groups.length / rowItems) + (groups.length % rowItems === 0 ? 0 : 1);
        const rowCount = hasNext ? realRowsCount + 1 : realRowsCount;

        if (!onLoad) {
            // We have no onLoad function so there is no necessary to use infinite list
            return (
                <>
                    {Array.from(Array(rowCount).keys()).map((index) =>
                        this.renderRow({
                            index,
                            key: index.toString(),
                            // Ignored props
                            style: {},
                            columnIndex: 0,
                            isScrolling: null,
                            isVisible: null,
                            parent: null,
                        }),
                    )}
                </>
            );
        }

        return (
            <>
                {groups.length ? (
                    <InfiniteListLoadable
                        isRowLoaded={this.isRowLoaded}
                        loadMoreRows={this.loadNext}
                        rowCount={rowCount}
                        rowRenderer={this.renderRow}
                        rowHeight={ROW_HEIGHT + (isMobile ? 5 : 10) /* margin-bottom */}
                        numberOfItemsOnFirstPage={10}
                    />
                ) : null}
            </>
        );
    };

    private isRowLoaded = ({ index }) =>
        !this.props.hasNext || (index + 1) * this.getRowItemsCount() <= this.props.groups.length;

    private renderRow = (listRowProps: ListRowProps) => {
        const {
            askedForMembership,
            groups,
            joining,
            location,
            memberships,
            onJoinGroup,
            requestUser,
            newBadge,
        } = this.props;
        const { index, key, style } = listRowProps;

        const rowItems = this.getRowItemsCount();
        const rowGroups = groups
            .slice(index * rowItems, (index + 1) * rowItems)
            .map((group) => (
                <GroupGridItem
                    activeMembersCount={group.activeMembersCount}
                    className={styles.GroupsGrid__row__item}
                    coverPhoto={group.coverPhoto}
                    description={group.description}
                    hasAskedForMembership={askedForMembership.includes(group.id)}
                    id={group.id}
                    isJoining={joining.includes(group.id)}
                    isMember={memberships.includes(group.id)}
                    key={group.id}
                    location={location}
                    locked={group.locked}
                    onJoinGroup={onJoinGroup}
                    requestUser={requestUser}
                    title={group.name}
                    newBadge={newBadge}
                />
            ));

        while (rowGroups.length < rowItems) {
            rowGroups.push(
                !this.isRowLoaded({ index }) ? (
                    <GroupGridItemPlaceholder
                        key={`placeholder-${rowGroups.length}`}
                        className={styles.GroupsGrid__row__item}
                    />
                ) : (
                    <div
                        key={`empty-${rowGroups.length}`}
                        className={styles.GroupsGrid__row__item}
                    />
                ),
            );
        }

        return (
            <div key={key} style={style}>
                <div className={styles.GroupsGrid__row}>{rowGroups}</div>
            </div>
        );
    };

    // NOTE: If changed - fix also max-width calc for row__item
    private getRowItemsCount() {
        return this.props.isMobile ? 2 : 3;
    }
}
